import { useNotificationsQuery } from "@graphql/notifications/Notifications.g";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import { NotificationTypeMappingText } from "@components/Texts";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { Icon } from "@design-system/Icon/Icon";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Resource } from "@design-system/Resource/Resource";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import classes from "./RecentUnresolvedNotificationsDropdown.module.css";

type Props = {
  count?: number;
};

export const RecentUnresolvedNotificationsDropdown = ({ count }: Props) => {
  const navigate = useNavigate();

  const notificationsQuery = useNotificationsQuery({
    resolvedAt_Isnull: true,
    sortBy: "-created_at",
    first: count || 5,
  });
  const unresolvedNotifications = notificationsQuery.data;

  const totalCount = unresolvedNotifications?.notifications?.totalCount ?? 0;
  const shownCount = unresolvedNotifications?.notifications?.edges.length ?? 0;
  const refetchNotifications = notificationsQuery.refetch;

  const handleNotificationClick = useCallback(
    (clientId: string, applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.$buildPath({
          params: {
            clientId,
            applicationId,
          },
        }),
        { viewTransition: true },
      );
      refetchNotifications();
    },
    [refetchNotifications, navigate],
  );

  return (
    <Dropdown
      trigger={
        <IconButton
          icon="faBell"
          bubbleCount={totalCount > 0 ? totalCount : undefined}
        />
      }
    >
      <Box padding="2px" direction="column" gap={2}>
        {shownCount && unresolvedNotifications ? (
          <>
            {unresolvedNotifications.notifications?.edges.map(
              (notificationNode) => (
                <Dropdown.Option
                  label="View More"
                  onSelect={() => {
                    if (
                      !notificationNode?.node?.client ||
                      !notificationNode?.node?.application
                    ) {
                      return;
                    }

                    handleNotificationClick(
                      notificationNode?.node?.client.id,
                      notificationNode?.node?.application.id,
                    );
                  }}
                >
                  {notificationNode?.node?.notificationType &&
                  notificationNode?.node?.application?.resource.productName ? (
                    <Box direction="column" gap={4} stretch={true}>
                      <Text size={10} color="textSecondary">
                        <NotificationTypeMappingText
                          notificationType={
                            notificationNode?.node?.notificationType
                          }
                        />
                      </Text>

                      <Resource
                        key={notificationNode?.node.id}
                        name={
                          notificationNode?.node?.application?.resource
                            .productName
                        }
                        subtext={notificationNode?.node?.client?.name}
                        signedLogoUrl={
                          notificationNode?.node?.application?.resource
                            .publicResource?.signedLogoUrl || ""
                        }
                      />
                    </Box>
                  ) : null}
                </Dropdown.Option>
              ),
            )}

            {totalCount > shownCount ? (
              <div className={classes.footerWrapper} />
            ) : null}

            {totalCount > shownCount && (
              <Dropdown.Option
                label="View More"
                onSelect={() => {
                  navigate(routes.portal.notifications.$path(), {
                    viewTransition: true,
                  });
                }}
              >
                <Box
                  justify="space-between"
                  align="center"
                  stretch={true}
                  gap={2}
                >
                  <Text color="textSecondary">
                    View {totalCount - shownCount} more
                  </Text>
                  <Text color="textSecondary">
                    <Icon icon="faAngleRight" />
                  </Text>
                </Box>
              </Dropdown.Option>
            )}
          </>
        ) : (
          <Box>
            <Text color="textSecondary">No unresolved notifications</Text>
          </Box>
        )}
      </Box>
    </Dropdown>
  );
};
